define('bindingHandlers/autocomplete',[
    'lib/lodash4',
    'lib/knockout',
    'jquery',
    'util/keyCode',
    'util/externalInclude',
    'services/loggingService'
], function (_, ko, $, kc, include, LOG) {
    'use strict';

    var AUTOCOMPLETE_DOMDATA_KEY = "autocomplete-binding-domdata";
    var EVENT_NAMESPACE = ".autocomplete-event";

    //
    // Util Methods
    //

    /*
        Get the value for a key stored on element's DOM object
    */
    var getDomDataValue = function (element, key) {
        var domData = ko.utils.domData.get(element, AUTOCOMPLETE_DOMDATA_KEY);
        return domData[key];
    };

    /*
        Update the value for a key on the data stored on a DOM object.
    */
    var updateDomData = function (element, key, value) {
        var domData = ko.utils.domData.get(element, AUTOCOMPLETE_DOMDATA_KEY);
        domData[key] = value;

        ko.utils.domData.set(element, AUTOCOMPLETE_DOMDATA_KEY, domData);
    };

    // ===================================================================================
    // Binding Handler Definition
    // ===================================================================================

    /**
     * <p>Binding for PureCloud's custom autocomplete box</p>
     * <p>
     * This binding adds a custom autocomplete dropdown box to forms. This binding handler
     * hooks into the value binding, so it is necessary to add both. To utilize this
     * binding handler, add the following to your HTML tag's data-bind attribute:
     *
     *    data-bind="value: observableInputValue,
     *                autocomplete: {
     *                   results: filteredResults,
     *                   template: '/path/to/listTemplate'
     *                }"
     *
     * </p>
     * <p>
     * Required Arguments
     *
     * <ul>
     *     <li>results - An observable variable that will contain the list of items that should be rendered in the autocomplete list.</li>
     *     <li>
                template - The path to the template that will be used to render the autocomplete list. The binding context of this template will be the same as the element that this binding handler is attached to.
                           NOTE: To take advantage of some of the styling and functionality that this binding handler provides, you need to use the following CSS classes in your template:
                                * autocomplete-list - This class should go on the root element of your list/table.
                                * autocomplete-item - This class needs to go onto each item in the results list.

                                On top of this, for each result item in your list, you need to add the following attribute to the node with the "autocomplete-item" class on it:

                                    data-autocomplete-value="itemValue"

                                Where "itemValue" would be the value that the autocomplete handler should use to update the value the text input form that it is attached to.
            </li>
     * </ul>
     * </p>
     * <p>
     * Optional Arguments
     *
     * On top of the required template, in addition to the required data-autocomplete-value="itemValue" attribute, you can also add an option attribute:
     *                              data-autocomplete-id="itemID"
     * Where itemID is the ID for the user, queue, etc you want to also pass to the submit argument
     *
     * <ul>
     *     <li>displayWithEmptyResults (Boolean) - A flag to tell the binding handler that it should display the autocomplete box when there are zero results to display. Pass true to display the empty list, false if not. This value defaults to false.</li>
     * </ul>
     * </p>
     *
     * @exports bindingHandlers/autocomplete
     */
    ko.bindingHandlers.autocomplete = {
        init: function (element, valueAccessor, allBindingsAccessor, viewModel, bindingContext) {
            //
            // Binding Handler Arguements
            //
            var bindingData = ko.utils.unwrapObservable(valueAccessor());

            var resultsSet = bindingData.results ? bindingData.results : bindingData;
            var displayWithEmptyResults = bindingData.displayWithEmptyResults;
            var input = allBindingsAccessor.get('value');
            var templateName = ko.unwrap(bindingData.template);

            if (!input) {
                LOG.error('The autocomplete binding needs to be preceded by a value binding.');
            }

            //
            // Private Variables
            //
            var $autocompleteNode;     // The DOM node that will contain the autocomplete
                                       // list and will be wrapped in a jQuery node
            var $element = $(element); // jQuery object of the binding's element
            var timeoutId;
            var shouldRunElementBlur = true;
            var originalValue;

            // Used to track the highlighted value when the user uses an arrow key to
            // select an item. This is used later to update the input observable if the
            // user blurs the element, otherwise the list would update with new value as
            // the user highlights options.
            var tempHighlightValue;

            ko.utils.domData.set(element, AUTOCOMPLETE_DOMDATA_KEY, {
                /*
                 Used to track which item in the autocomplete list is highlighted.
                 This value should get reset to -1 every time the resulsSet becomes empty or
                 the autocomplete list is hidden.
                */
                keydownIndex: -1,

                /*
                    Used to track when we should display the autocomplete list. We want to
                    always display it, except in the following situations:

                    - When a user highlights an item in the list and presses "enter" or "esc"
                    - When a user clicks on an item in the list, the element should be blurred
                      and the list hidden. If the user then refocuses on the element, the list
                      should stay hidden.
                */
                shouldDisplayList: true
            });

            //
            // Event Callbacks
            //

            // This callback will execute when the user clicks on an item in the
            // autocomplete list.
            var listClickCallback = function (evt) {
                var $target = $(evt.target);
                var value = $target.closest(".autocomplete-item").attr("data-autocomplete-value");
                var id = $target.closest(".autocomplete-item").attr("data-autocomplete-id");

                if (value) {
                    $element.val(value);
                    input(value);
                    hideAutocompleteBox();
                    updateDomData(element, "shouldDisplayList", false);
                    if (bindingData.submit) {
                        bindingData.submit.call(this, value, evt, id);
                    } else {
                        $element.closest('form').submit();
                    }
                }
            };

            var listKeyupCallback = function (evt) {
                var keyCode = evt.which;

                if (!isActionKeyCode(keyCode)) {
                    originalValue = $element.val();
                }
            };

            /*
                This callback will execute whenever the element passed into the handler
                has focus and the user triggers a keydown event. It will result in the
                following outcomes:

                    * If the user presses a key with the keycode representing UP or DOWN,
                      then we will update the highlighted item in the autocomplete list
                      (if it's displayed)
                    * If the user presses ENTER or ESCAPE, then we will hide the
                      autocomplete list update the element's value and update the input()
                      observable with the element's latest value.
                    * If the user presses any other key (like a letter), then we will take
                      the element's new value and update the input() observable.
            */
            var listKeydownCallback = function (evt) {
                clearTimeout(timeoutId); // a new keydown event occurred, so clear out the

                var keyCode = evt.which;

                if (!isAutocompleteHidden() && isActionKeyCode(keyCode)) {
                    // Update the index of the item to highlight in the results list.
                    var $itemCollection = $find(".autocomplete-item");
                    var keydownIndex = getDomDataValue(element, "keydownIndex");

                    if (keyCode === kc.DOWN) {
                        keydownIndex++;

                        if (keydownIndex >= $itemCollection.length) {
                            keydownIndex = 0;
                        }
                    } else if (keyCode === kc.UP) {
                        keydownIndex--;

                        if (keydownIndex < 0) {
                            keydownIndex = $itemCollection.length - 1;
                        }

                        // prevents moving cursor to beginning/end of the text field in some browsers
                        evt.preventDefault();
                    } else if (keyCode === kc.ENTER || keyCode === kc.ESCAPE) {
                        updateDomData(element, "shouldDisplayList", false);
                        if (keyCode === kc.ENTER) {
                            updateInputValue(true);
                        } else if (originalValue) {
                            // for ESC we want to set the value back to the original search term that was typed in
                            $element.val(originalValue);
                            if (originalValue !== ko.unwrap(input)) {
                                input(originalValue);
                            }
                        }
                        hideAutocompleteBox();
                        tempHighlightValue = null;

                        // Add an optional submit argument that will prevent enter from bubbling up to the form submit. Since it
                        // is not valid to nest <form> elements, this will allow an autocomplete field to exist in a larger form.
                        if (bindingData.submit) {
                            if (evt.keyCode === kc.ENTER) {
                                var id, autocompleteObject = $($itemCollection[keydownIndex])[0];
                                if (autocompleteObject) {
                                    id = autocompleteObject.getAttribute('data-autocomplete-id');
                                }

                                var data = $(evt.target).closest('input').val();

                                bindingData.submit.call(this, data, evt, id);
                            }
                        }
                        return;
                    }

                    if (keydownIndex > -1) {


                        var node = $find(".autocomplete-item").get(keydownIndex);
                        var $node = $(node);

                        if ($node && !$node.hasClass("autocomplete-highlight")) {
                            var $prevFocusNode = $find(".autocomplete-highlight");
                            if ($prevFocusNode) {
                                $prevFocusNode.removeClass("autocomplete-highlight");
                            }

                            $node.addClass("autocomplete-highlight");

                            // Update the form's value
                            var value = $node.attr("data-autocomplete-value");
                            if (value) {
                                $element.val(value);
                                tempHighlightValue = value;
                            }
                        }
                    }

                    updateDomData(element, "keydownIndex", keydownIndex);
                } else {
                    updateDomData(element, "shouldDisplayList", true);
                    updateInputValue();
                }
            };

            //
            // Util Methods
            //

            var displayAutocompleteBox = function () {
                $autocompleteNode.removeClass("hide");
            };

            var hideAutocompleteBox = function () {
                $autocompleteNode.addClass("hide");
                updateDomData(element, "keydownIndex", -1);
            };

            /*
                Wrapper method to help with finding nodes with the special autocomplete-*
                classes by containing the search to the element's parent node.
            */
            var $find = function (selector) {
                return $element.parent().find(selector);
            };

            /*
                Determine if a key code is one of the action key's that we perform a
                special action on.
            */
            var isActionKeyCode = function (keyCode) {
                return keyCode === kc.UP    ||
                       keyCode === kc.DOWN  ||
                       keyCode === kc.ENTER ||
                       keyCode === kc.ESCAPE;
            };

            var isAutocompleteHidden = function () {
                return $autocompleteNode.hasClass("hide");
            };

            /*
                Cleanup method for when the element's DOM is removed.
            */
            var onDisposal = function () {
                // clean up events
                $autocompleteNode.off(EVENT_NAMESPACE);
                $element.off(EVENT_NAMESPACE);
            };

            /*
                Wrapper method to help with updating the input observable with the latest
                value in the element.
            */
            var updateInputValue = function (updateNow) {
                function updateValue () {
                    var elementValue = $element.val();

                    if (elementValue !== ko.unwrap(input)) {
                        input(elementValue);
                    }
                }

                clearTimeout(timeoutId);

                // The user is typing in the input box, so update the observable
                // that is monitoring the input value when they finish typing.
                if (updateNow) {
                    updateValue();
                } else {
                    timeoutId = setTimeout(updateValue, 200);
                }
            };

            // ==========
            // Main Logic
            // ==========
            // add a css module class to the parent
            $element.parent().addClass('autocomplete-wrapper');

            // turn off the browser's default autocomplete dropdown.
            $element.attr("autocomplete", "off");

            //
            // Create the DOM
            //
            $autocompleteNode = $("<div>").addClass("autocomplete-container")
                                              .appendTo($element.parent());
            hideAutocompleteBox();

            //
            // Add event handlers to the element
            //
            $element.on('keydown' + EVENT_NAMESPACE, listKeydownCallback);
            $element.on('keyup' + EVENT_NAMESPACE, listKeyupCallback);
            $element.on('blur' + EVENT_NAMESPACE, function () {
                // Check to see if we should run the blur event because hiding the
                // autocomplete list in the blur event will prevent the click callback
                // on the list from executing. This helps us achieve the following
                // interaction:
                //
                //  1. The click event on the autocomplete list runs
                //  2. A user presses an arrow key to highlight an item and then blurs the
                //     element they were focused on. This will update the element's value
                //     to be the item they highlighted and hide the autocomplete list.
                if (shouldRunElementBlur) {
                    // If the user blur's the input, the autocomplete box should be hidden
                    // and not reshown until they start typing again

                    hideAutocompleteBox();
                    updateDomData(element, "shouldDisplayList", false);

                    var elementValue;
                    if (tempHighlightValue) {
                        // If the user has highlighted a list item with the arrow keys and
                        // triggers blur, make sure the element and input observable are
                        // updated to the highlight.
                        elementValue = tempHighlightValue;
                        $element.val(elementValue);
                    } else {
                        // If the user just typed a normal key and blurred the element,
                        // then make sure that string stays as it is and is not updated
                        // to the tempValue var we created.
                        elementValue = $element.val();
                    }

                    if (elementValue !== ko.unwrap(input)) {
                        input(elementValue);
                    }

                    tempHighlightValue = null;
                } else if (resultsSet().length === 0 && displayWithEmptyResults) {
                    // If a click event occurred on the autocomplete node and we are
                    // displaying an empty result list, then we should not hide the list.
                    $element.focus();
                }

                shouldRunElementBlur = true;
            });

            //
            // Add event handlers to the new autocomplete node
            //
            $autocompleteNode.on('click' + EVENT_NAMESPACE, listClickCallback);
            $autocompleteNode.on('mousedown' + EVENT_NAMESPACE, function () {
                shouldRunElementBlur = false;
            });

            //
            // Render the Template if there are any results
            //
            if (resultsSet.length > 0) {
                include.loadTemplate(templateName).then(function () {
                    ko.renderTemplate(templateName, bindingContext, {}, $autocompleteNode, "replaceChildren");
                    displayAutocompleteBox();
                });
            }

            // Clean up the binding handler when the element is removed from the DOM.
            ko.utils.domNodeDisposal.addDisposeCallback(element, onDisposal);
        },
        update: function (element, valueAccessor, allBindingsAccessor, viewModel, bindingContext) {
            //
            // Binding Handler Arguements
            //
            var bindingData = ko.utils.unwrapObservable(valueAccessor());

            var resultsSet = bindingData.results ? ko.unwrap(bindingData.results) : bindingData;
            var displayWithEmptyResults = bindingData.displayWithEmptyResults;
            var templateName = ko.unwrap(bindingData.template);
            var input = allBindingsAccessor.get('value');

            if (!input) {
                LOG.error('The autocomplete binding needs to be preceded by a value binding.');
            }

            //
            // Private variables
            //
            var $element = $(element);
            var $autocompleteNode = $element.parent().find(".autocomplete-container");

            //
            // Util
            //
            var hideAutocompleteBox = function () {
                $autocompleteNode.addClass("hide");
                updateDomData(element, "keydownIndex", -1);
            };

            var shouldDisplayList = function () {
                return getDomDataValue(element, "shouldDisplayList");
            };

            var shouldDisplayResults = function () {
                return input() !== "" && (displayWithEmptyResults || resultsSet.length > 0);
            };

            // ==========
            // Main Logic
            // ==========

            if ($element.is(":focus") && shouldDisplayResults()) {
                include.loadTemplate(templateName).then(function () {
                    ko.renderTemplate(templateName, bindingContext, {}, $autocompleteNode[0], "replaceChildren");

                    if (shouldDisplayList()) {
                        $autocompleteNode.removeClass("hide");
                    } else {
                        hideAutocompleteBox();
                    }
                });
            } else if (resultsSet.length < 1) {
                // hide autocomplete box
                hideAutocompleteBox();
            }
        }
    };
});

